import { useCallback, useEffect } from 'react';

export function useStickyOffset() {
  const setTopElementHeight = useCallback(
    (elementRef: React.RefObject<HTMLElement>, variable: string) => {
      if (elementRef.current) {
        const container = elementRef.current.getBoundingClientRect();
        const computedStyle = window.getComputedStyle(elementRef.current);
        const heightAdjustment = getSizeAdjustmentForNegativeMargins(computedStyle);
        const height = container.height + heightAdjustment;
        document.documentElement.style.setProperty(variable, `${height}px`);
      }
    },
    []
  );

  const getSizeAdjustmentForNegativeMargins = (style: CSSStyleDeclaration) => {
    const marginTop = parseFloat(style.marginTop || '0');
    const marginBottom = parseFloat(style.marginBottom || '0');
    const negativeMarginTop = marginTop < 0 ? marginTop : 0;
    const negativeMarginBottom = marginBottom < 0 ? marginBottom : 0;
    return negativeMarginBottom + negativeMarginTop;
  };

  const useResizeListener = (
    resizeFunction: () => void,
    elementReference: React.RefObject<HTMLElement>
  ) => {
    useEffect(() => {
      window.addEventListener('resize', resizeFunction);
      const resizeObserver = new ResizeObserver(resizeFunction);
      if (elementReference.current) {
        resizeObserver.observe(elementReference.current);
      }
      return () => {
        window.removeEventListener('resize', resizeFunction);
        resizeObserver.disconnect();
      };
    }, [resizeFunction, elementReference]);
  };

  const setSecondaryStickyElementHeight = useCallback(
    (
      topElementHeightClass: string,
      targetElementHeightClass: string,
      additionalComponentRef?: React.RefObject<HTMLElement>
    ) => {
      const baseValue = getComputedStyle(document.documentElement)
        .getPropertyValue(topElementHeightClass)
        .replace('px', '')
        .trim();
      const baseNum = parseFloat(baseValue) || 0;

      let extraHeight = 0;
      if (additionalComponentRef?.current) {
        const container = additionalComponentRef.current.getBoundingClientRect();
        const style = window.getComputedStyle(additionalComponentRef.current);
        const adjust = getSizeAdjustmentForNegativeMargins(style);
        extraHeight = container.height + adjust;
      }

      document.documentElement.style.setProperty(
        targetElementHeightClass,
        `${baseNum + extraHeight}px`
      );
    },
    []
  );

  return { setTopElementHeight, setSecondaryStickyElementHeight, useResizeListener };
}
